import * as rootLogger from 'loglevel';
import { LogEntry, LogEntryMeta, newLogEntry, RequestLogEntry } from '../core/logEntry';
import { CORRELATION_META_KEY, Logger } from '../core/logger';
import { BaseLoggerConfig } from '../core/config';
import { LogLevel } from '../core/levels';
import { generateCorrelationId as _generateCorrelationId } from '../correlationId';
import { apply } from './plugins/apply';
import { PluginFunction } from './plugins/plugin';

/**
 * LogLevelConfig is the configuration required by a LogLevelConfig instance.
 */
type LogLevelConfig = BaseLoggerConfig & {
  plugins: PluginFunction[];
  parent?: rootLogger.Logger;
};

/**
 * LogLevelLogger is a `Logger` implementation using the simple loglevel
 * library.
 *
 * If `parent` is provided, it will be used as the parent logger. Otherwise,
 * a new logger will be created from the underlying loglevel library. As a
 * result _ALL_ logger instances will share the same underlying config. If you
 * need to change something, like log level, you must create a new logger.
 *
 * https://github.com/pimterry/loglevel
 */
class LogLevelLogger implements Logger {
  private config;
  private logger;

  defaultMeta: Partial<LogEntryMeta>;

  private static getLoggerName({ environment, service }: { environment: string; service: string }) {
    return `${environment}:${service}:${this.generateCorrelationId()}`;
  }

  private newLogLevelLoggerInstance() {
    const { environment, service, level, plugins } = this.config;
    // Create a new loglevel logger from the root logger
    const logger = rootLogger.getLogger(LogLevelLogger.getLoggerName({ environment, service }));

    // Apply the plugins
    apply(logger, plugins);

    // Set the logger level
    logger.setLevel(level);

    return logger;
  }

  constructor({ parent, ...config }: LogLevelConfig) {
    // Shallow copy config for child logger creation (if needed)
    this.config = config;

    // Set the logger instance if it doesn't already exist
    this.logger = parent || this.newLogLevelLoggerInstance();

    const { environment, service, defaultMeta = {} } = config;

    // Set the default meta based on special config values and any provided
    // default meta;
    this.defaultMeta = {
      environment,
      service,
      ...defaultMeta,
    };
  }

  getMeta<T>(key: string): T | undefined {
    const val = this.defaultMeta?.[key];
    if (val !== undefined) {
      return val as T;
    }

    return undefined;
  }

  appendMeta(meta: Partial<LogEntryMeta>): void {
    this.defaultMeta = { ...this.defaultMeta, ...meta };
  }

  static generateCorrelationId() {
    return _generateCorrelationId();
  }

  generateCorrelationId() {
    return LogLevelLogger.generateCorrelationId();
  }

  getCorrelationId(): string | undefined {
    return this.getMeta(CORRELATION_META_KEY);
  }

  setCorrelationId(id?: string | undefined): void {
    this.appendMeta({
      [CORRELATION_META_KEY]: id || this.generateCorrelationId(),
    });
  }

  child(additionalMeta: Partial<LogEntryMeta>): Logger {
    // Create the new logger with the same config
    const childLogger = new LogLevelLogger({ ...this.config, parent: this.logger });

    // Copy the existing meta data
    childLogger.appendMeta(this.defaultMeta);

    // Copy the new meta data
    childLogger.appendMeta(additionalMeta);

    return childLogger;
  }

  private newLogEntry(entryOrMessage: LogEntry | LogEntry['message'], meta: LogEntryMeta = {}): LogEntry {
    return {
      ...this.defaultMeta,
      ...newLogEntry(entryOrMessage, meta),
    };
  }

  trace(entryOrMessage: LogEntry | LogEntry['message'], meta: LogEntryMeta = {}): void {
    this.logger.trace(this.newLogEntry(entryOrMessage, meta));
  }

  debug(entryOrMessage: LogEntry | LogEntry['message'], meta: LogEntryMeta = {}): void {
    this.logger.debug(this.newLogEntry(entryOrMessage, meta));
  }

  info(entryOrMessage: LogEntry | LogEntry['message'], meta: LogEntryMeta = {}): void {
    this.logger.info(this.newLogEntry(entryOrMessage, meta));
  }

  warn(entryOrMessage: LogEntry | LogEntry['message'], meta: LogEntryMeta = {}): void {
    this.logger.warn(this.newLogEntry(entryOrMessage, meta));
  }

  error(entryOrMessage: LogEntry | LogEntry['message'], meta: LogEntryMeta = {}): void {
    this.logger.error(this.newLogEntry(entryOrMessage, meta));
  }

  log({ level, message, ...meta }: LogEntry) {
    // HACK: The loglevel logger uses "log" as a debug level vs an arbitrary
    // level defined by the meta. This is a hack to make log be able to log
    // at any log level. Types can probably be cleaned up.
    switch (level) {
      case LogLevel.TRACE:
        this.trace(message, meta);
        return;
      case LogLevel.DEBUG:
        this.debug(message, meta);
        return;
      case LogLevel.INFO:
        this.info(message, meta);
        return;
      case LogLevel.WARN:
        this.warn(message, meta);
        return;
      case LogLevel.ERROR:
        this.error(message, meta);
        return;
      default:
        this.info(message, meta);
    }
  }

  /**
   * request logs a HTTP request and sets the appropriate level based on its status.
   * See request plugin for normalizing the status and method fields.
   */
  request(entry: RequestLogEntry): void {
    const { status } = entry;

    // If it's less than 400, info it
    if (status && status < 400) {
      this.info(entry);
      return;
      // If it's less than 500 and not a 401, warn it
    } else if (status && status < 500 && status != 401) {
      this.warn(entry);
      return;
    }

    // Anything else, error it
    return this.error(entry);
  }
}

export type { LogLevelConfig };
export { LogLevelLogger };
